import { normalizeProductFamily } from '../../constants/products';

export type FlexPlan = {
  family: string;
  planCode: string;
  trialLength?: number;
  term?: string;
  discountPercentage?: number;
};

export type FlexUpgradePlan<TPlan extends FlexPlan> = {
  id?: string;
  family: string;
  product: TPlan;
};

/**
 * Finds the flex plan for a product family from the getAllProducts eligiblePlans.
 *
 * Trial eligible users are moved onto the trial variant of a family (trialLength > 0), everyone
 * else onto the standard variant. Falls back to the first plan of the family so a missing variant
 * doesn't leave a journey without a plan.
 *
 * @param family - Product family e.g. 'identity' or 'credit_expert'
 * @param eligiblePlans - eligiblePlans from useGetAllProductsQuery
 * @param isTrialEligible - isTrialEligible from useGetAllProductsQuery
 * @returns The matching plan, or null when the family has no eligible plans
 */
const findFlexPlanByFamily = <TPlan extends FlexPlan>(
  family?: string,
  eligiblePlans?: TPlan[],
  isTrialEligible?: boolean
): TPlan | null => {
  if (!family || !Array.isArray(eligiblePlans)) return null;

  const plans = eligiblePlans.filter(plan => normalizeProductFamily(plan?.family) === family);

  if (!plans.length) return null;

  const isTrialPlan = (plan: TPlan) => (plan?.trialLength ?? 0) > 0;

  return plans.find(plan => (isTrialEligible ? isTrialPlan(plan) : !isTrialPlan(plan))) ?? plans[0];
};

export const getFlexUpgradePlan = <TPlan extends FlexPlan>(
  family?: string,
  eligiblePlans?: TPlan[],
  isTrialEligible?: boolean
): FlexUpgradePlan<TPlan> | null => {
  const product = findFlexPlanByFamily(family, eligiblePlans, isTrialEligible);

  return product
    ? {
        id: product.planCode,
        family: product.family,
        product,
      }
    : null;
};

export const MONTHLY_TERM = 'MONTHLY';
export const ANNUAL_TERM = 'ANNUALLY';

const TERM_BY_BILLING_PERIOD: Record<string, string> = {
  monthly: MONTHLY_TERM,
  annual: ANNUAL_TERM,
};

export type FamilyBillingPeriods<TPlan extends FlexPlan> = {
  monthlyPlan: TPlan | null;
  annualPlan: TPlan | null;
  hasBothBillingPeriods: boolean;
};

/**
 * Reports which billing periods a family offers, so journeys can decide whether a billing period
 * choice is available instead of hardcoding which families have an annual plan.
 *
 * @param family - Product family e.g. 'identity'
 * @param eligiblePlans - eligiblePlans from useGetAllProductsQuery
 */
export const getFamilyBillingPeriods = <TPlan extends FlexPlan>(
  family?: string,
  eligiblePlans?: TPlan[]
): FamilyBillingPeriods<TPlan> => {
  const plans = (Array.isArray(eligiblePlans) ? eligiblePlans : []).filter(
    plan => Boolean(family) && normalizeProductFamily(plan?.family) === family
  );

  const monthlyPlan = plans.find(plan => plan?.term?.toUpperCase() === MONTHLY_TERM) ?? null;
  const annualPlan = plans.find(plan => plan?.term?.toUpperCase() === ANNUAL_TERM) ?? null;

  return {
    monthlyPlan,
    annualPlan,
    hasBothBillingPeriods: Boolean(monthlyPlan && annualPlan),
  };
};

/**
 * Finds the flex plan for a family on a specific billing period, for journeys where the term matters
 * (e.g. changing billing period) rather than just the family.
 *
 * @param family - Product family e.g. 'identity'
 * @param billingPeriod - 'monthly' or 'annual'
 * @param eligiblePlans - eligiblePlans from useGetAllProductsQuery
 * @param isTrialEligible - isTrialEligible from useGetAllProductsQuery
 * @returns The matching plan wrapped as an upgrade plan, or null when no plan matches
 */
export const getFlexPlanByFamilyAndPeriod = <TPlan extends FlexPlan>(
  family?: string,
  billingPeriod?: string,
  eligiblePlans?: TPlan[],
  isTrialEligible?: boolean
): FlexUpgradePlan<TPlan> | null => {
  const targetTerm = TERM_BY_BILLING_PERIOD[billingPeriod ?? ''];

  if (!family || !targetTerm || !Array.isArray(eligiblePlans)) return null;

  const plans = eligiblePlans.filter(
    plan => normalizeProductFamily(plan?.family) === family && plan?.term?.toUpperCase() === targetTerm
  );

  if (!plans.length) return null;

  const isTrialPlan = (plan: TPlan) => (plan?.trialLength ?? 0) > 0;
  const product = plans.find(plan => (isTrialEligible ? isTrialPlan(plan) : !isTrialPlan(plan))) ?? plans[0];

  return {
    id: product.planCode,
    family: product.family,
    product,
  };
};

export default findFlexPlanByFamily;
